import React from 'react';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {LanguageItem} from 'utils/i18n/types';
import {preloadAndSetDayJsLocale} from '@chekin/core';
import ModalSheet from 'components/common/ModalSheet';
import {languageOptions} from 'utils/i18n/constants';
import {LanguageItemButton, List, Title} from './styled';
import {useModalControls} from '@chekin/guestapp-ui-kit';

export type LanguageSelectionRef = {
  open: () => void;
};

const LanguageSelection = React.forwardRef<LanguageSelectionRef, unknown>((_, ref) => {
  const {t} = useTranslation();
  const {
    isOpen: isOpenLanguageSelection,
    closeModal: closeLanguageSelection,
    openModal: openLanguageSelection,
  } = useModalControls();

  const [activeLanguageOption, setActiveLanguageOption] = React.useState(
    languageOptions[0],
  );

  React.useLayoutEffect(function updateLanguageOption() {
    const nextOption = languageOptions.find(option => {
      return option.value === i18n.language.slice(0, 2);
    });
    if (nextOption) {
      setActiveLanguageOption(nextOption);
    }
  }, []);

  const changeLanguage = React.useCallback(
    async (language: LanguageItem) => {
      setActiveLanguageOption(language);
      await preloadAndSetDayJsLocale(language.value);
      void i18n.changeLanguage(language.value);
      closeLanguageSelection();
    },
    [setActiveLanguageOption, closeLanguageSelection],
  );

  React.useImperativeHandle(
    ref,
    () => {
      return {
        open: openLanguageSelection,
      };
    },
    [openLanguageSelection],
  );

  return (
    <ModalSheet
      isOpenModalSheet={isOpenLanguageSelection}
      handleCloseModal={closeLanguageSelection}
      closeOnDocumentClick
      className="language-modal"
      withCloseButton
    >
      <Title className="modal__title language-modal__title kiosko:text-kiosko-m kiosko:mb-[68px]">
        {t('select_your_language')}
      </Title>
      <List className="language-modal__list">
        {languageOptions.map((x, index) => (
          <LanguageItemButton
            selected={activeLanguageOption.value === x.value}
            key={index}
            onClick={() => changeLanguage(x)}
            className="language-modal__option kiosko:p-6"
          >
            <div className="kiosko:text-kiosko-xs">{x.label}</div>
          </LanguageItemButton>
        ))}
      </List>
    </ModalSheet>
  );
});

export {LanguageSelection};
